import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import BillingBalance from '../billing/BillingBalance';
import {
  FiArrowRight,
  FiAward,
  FiBarChart2,
  FiBookOpen,
  FiCheckSquare,
  FiClipboard,
  FiEdit3,
  FiFileText,
  FiHome,
  FiSearch,
  FiX,
} from 'react-icons/fi';
import {
  FaBookOpen,
  FaFlaskVial,
  FaLanguage,
  FaPenNib,
  FaSquareRootVariable,
} from 'react-icons/fa6';
import { useNavigate } from 'react-router-dom';
import EssayHistory from './components/EssayHistory';
import EssayWorkspace from './components/EssayWorkspace';
import './AiEssay.scss';

const subjects = [
  {
    id: 'math', name: '수학', icon: FaSquareRootVariable, summary: '수학 서술형 · 논술',
    description: '수학 서술형과 논술 답안의 개념, 풀이 근거와 표현을 첨삭합니다.',
    color: '#2f78df', softColor: '#eaf3ff',
    answerMethods: ['사진 촬영', '이미지 업로드', '직접 입력', '수식 입력'],
    points: ['문항 요구에 맞는 서술', '수학적 개념과 근거', '풀이 과정의 논리성', '결론과 표현의 정확성'],
  },
  {
    id: 'science', name: '과학', icon: FaFlaskVial, summary: '과학 서술형 · 논술',
    description: '과학 서술형과 논술 답안의 개념, 근거와 논리 전개를 첨삭합니다.',
    color: '#38a65b', softColor: '#eaf8ee',
    answerMethods: ['사진 촬영', '이미지 업로드', '직접 입력', '음성 입력'],
    points: ['문항 요구에 맞는 서술', '과학 개념의 정확성', '자료와 근거의 활용', '결론 도출의 논리성'],
  },
  {
    id: 'korean', name: '국어', icon: FaBookOpen, summary: '논술 · 독해 · 서술형',
    description: '논리적 사고와 표현 능력을 종합적으로 첨삭합니다.',
    color: '#ff8a16', softColor: '#fff3e5',
    answerMethods: ['사진 촬영', '이미지 업로드', '직접 입력', '음성 입력'],
    points: ['주제 이해 및 주장', '논리적 전개와 구성', '근거 활용의 적절성', '표현 및 문법의 정확성'],
  },
  {
    id: 'english', name: '영어', icon: FaLanguage, summary: '영작 · 논술 · 서술형',
    description: '영어 문장의 구성과 표현을 문맥에 맞게 첨삭합니다.',
    color: '#7253d8', softColor: '#f0ecff',
    answerMethods: ['사진 촬영', '이미지 업로드', '직접 입력', '음성 입력'],
    points: ['문장 구조와 문법', '어휘 선택의 적절성', '내용 전개의 일관성', '자연스러운 영어 표현'],
  },
  {
    id: 'integrated', name: '통합 교과', icon: FaPenNib, summary: '교과 통합 · 창의 사고',
    description: '여러 교과를 연결한 이해와 근거, 창의적인 표현을 분석합니다.',
    color: '#ed4f73', softColor: '#fff0f4',
    answerMethods: ['사진 촬영', '이미지 업로드', '직접 입력', '음성 입력'],
    points: ['주제 통합 이해도', '창의적인 관점', '근거와 자료의 활용', '글 전체의 완성도'],
  },
];

const features = [
  { icon: FiEdit3, title: '서술형 첨삭', text: '핵심 개념과 정답 요소 분석' },
  { icon: FiFileText, title: '논술 첨삭', text: '주장·근거·구성·표현 분석' },
  { icon: FiSearch, title: '문제 자료 판독', text: '사진·이미지·PDF 입력 지원' },
  { icon: FiAward, title: '모범답안 제공', text: '우수 답안과 비교 학습' },
  { icon: FiCheckSquare, title: '유사문제 학습', text: '취약점 맞춤 문제 제공' },
  { icon: FiBarChart2, title: '상세한 분석', text: '평가 기준별 점수와 피드백' },
];

function SubjectCard({ subject, wide = false, onSelect }) {
  const SubjectIcon = subject.icon;

  return (
    <button
      type="button"
      className={`ai-essay-subject${wide ? ' ai-essay-subject--wide' : ''}`}
      style={{ '--subject-color': subject.color, '--subject-soft': subject.softColor }}
      onClick={() => onSelect(subject)}
    >
      <span className="ai-essay-subject__icon" aria-hidden="true"><SubjectIcon /></span>
      <span className="ai-essay-subject__copy">
        <strong>{subject.name}</strong>
        <small>{subject.summary}</small>
      </span>
      {wide && <FiArrowRight className="ai-essay-subject__arrow" aria-hidden="true" />}
    </button>
  );
}

function BottomNavigation({ activeItem, onHome, onHistory, onModelAnswers, barRef }) {
  return (
    <footer className="ai-essay-bottom-bar" ref={barRef}>
      <BillingBalance showWalletLink={false} />
      <nav className="ai-essay-bottom-nav" aria-label="AI 서술형 및 논술 첨삭 메뉴">
        <button type="button" className={activeItem === 'home' ? 'is-active' : ''} aria-current={activeItem === 'home' ? 'page' : undefined} onClick={onHome}><FiHome aria-hidden="true" /><span>홈</span></button>
        <button type="button" className={activeItem === 'history' ? 'is-active' : ''} aria-current={activeItem === 'history' ? 'page' : undefined} onClick={onHistory}><FiClipboard aria-hidden="true" /><span>첨삭 내역</span></button>
        <button type="button" className={activeItem === 'modelAnswers' ? 'is-active' : ''} aria-current={activeItem === 'modelAnswers' ? 'page' : undefined} onClick={onModelAnswers}><FiBookOpen aria-hidden="true" /><span>모범답안</span></button>
      </nav>
    </footer>
  );
}

function EssayHome({ onClose, onSelectSubject }) {
  return (
    <>
      <header className="ai-essay-home-header">
        <button type="button" className="ai-essay-home-header__close" onClick={onClose} title="닫기" aria-label="서비스 메뉴로 이동"><FiX aria-hidden="true" /></button>
      </header>

      <div className="ai-essay-home-content">
        <section className="ai-essay-hero" aria-labelledby="ai-essay-title">
          <h1 id="ai-essay-title"><em>AI</em> 서술형 및 논술 첨삭</h1>
          <p>서술형 답안과 논술을 각각의 기준으로 분석하고<br />구체적인 개선 방향을 제안해 드립니다.</p>
        </section>

        <section className="ai-essay-subject-section" aria-labelledby="subject-heading">
          <div className="ai-essay-section-heading">
            <div><h2 id="subject-heading">첨삭할 과목을 선택하세요</h2></div>
            <small>과목별 분석 기준이 적용됩니다.</small>
          </div>
          <div className="ai-essay-subject-grid">
            {subjects.slice(0, 4).map((subject) => (
              <SubjectCard key={subject.id} subject={subject} onSelect={onSelectSubject} />
            ))}
          </div>
          <SubjectCard subject={subjects[4]} wide onSelect={onSelectSubject} />
        </section>

        <section className="ai-essay-features" aria-labelledby="feature-heading">
          <div className="ai-essay-section-heading"><div><h2 id="feature-heading">AI 첨삭 기능</h2></div></div>
          <div className="ai-essay-feature-grid">
            {features.map(({ icon: Icon, title, text }) => (
              <article key={title}><span><Icon aria-hidden="true" /></span><div><h3>{title}</h3><p>{text}</p></div></article>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}

function AiEssay() {
  const navigate = useNavigate();
  const pageRef = useRef(null);
  const appRef = useRef(null);
  const bottomBarRef = useRef(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [activeView, setActiveView] = useState('home');

  useLayoutEffect(() => {
    const pageElement = pageRef.current;
    const appElement = appRef.current;
    if (!pageElement || !appElement) return undefined;

    const syncBottomNavigationBounds = () => {
      const appBounds = appElement.getBoundingClientRect();
      appElement.style.setProperty('--ai-essay-nav-left', `${appBounds.left}px`);
      appElement.style.setProperty('--ai-essay-nav-width', `${appBounds.width}px`);
      appElement.style.setProperty('--ai-essay-nav-transform', 'none');
      const barHeight = bottomBarRef.current?.offsetHeight;
      if (barHeight) appElement.style.setProperty('--ai-essay-bottom-bar-height', `${barHeight}px`);
    };

    syncBottomNavigationBounds();
    const resizeObserver = typeof ResizeObserver === 'function'
      ? new ResizeObserver(syncBottomNavigationBounds)
      : null;
    resizeObserver?.observe(pageElement);
    resizeObserver?.observe(appElement);
    if (bottomBarRef.current) resizeObserver?.observe(bottomBarRef.current);
    window.addEventListener('resize', syncBottomNavigationBounds);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', syncBottomNavigationBounds);
    };
  }, []);

  useEffect(() => {
    pageRef.current?.scrollTo({ top: 0, behavior: 'auto' });
    window.scrollTo({ top: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [activeView, selectedSubject]);

  const showHome = () => {
    setSelectedSubject(null);
    setActiveView('home');
    pageRef.current?.scrollTo({ top: 0, behavior: 'auto' });
  };

  const selectSubject = (subject) => {
    setSelectedSubject(subject);
    setActiveView('workspace');
  };

  const showHistory = () => {
    setSelectedSubject(null);
    setActiveView('history');
    pageRef.current?.scrollTo({ top: 0, behavior: 'auto' });
  };

  const showModelAnswers = () => {
    setSelectedSubject(null);
    setActiveView('modelAnswers');
    pageRef.current?.scrollTo({ top: 0, behavior: 'auto' });
  };

  return (
    <main className={`ai-essay-page${activeView !== 'home' ? ' is-detail' : ''}`} ref={pageRef}>
      <div className="ai-essay-app" ref={appRef} data-testid="ai-essay-app">
        {activeView === 'history'
          ? <EssayHistory key="history" subjects={subjects} onHome={showHome} />
          : activeView === 'modelAnswers'
            ? <EssayHistory key="modelAnswers" subjects={subjects} onHome={showHome} view="modelAnswers" />
            : selectedSubject
              ? <EssayWorkspace subject={selectedSubject} onBack={showHome} onHome={showHome} />
              : <EssayHome onClose={() => navigate('/menu')} onSelectSubject={selectSubject} />}
        <BottomNavigation
          barRef={bottomBarRef}
          activeItem={activeView === 'history' || activeView === 'modelAnswers' ? activeView : 'home'}
          onHome={showHome}
          onHistory={showHistory}
          onModelAnswers={showModelAnswers}
        />
      </div>
    </main>
  );
}

export default AiEssay;
